/* RESPONSIBLE TEAM: team-frontend-tech */

/*
 * ⚠️  WARNING: BRIDGE LOGIC - DO NOT MODIFY WITHOUT #team-frontend-tech APPROVAL ⚠️
 *
 * This modifier contains critical bridge logic that enables communication between the
 * Ember app (apps/embercom) and React app (apps/teammate-app). Any modifications
 * to this modifier can break the integration between the two applications.
 *
 * Before making any changes to this file, please reach out to #team-frontend-tech
 * for review and approval.
 */

/* eslint-disable promise/prefer-await-to-then */
import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';
import type Session from 'embercom/services/session';
import type IntlService from 'ember-intl/services/intl';
import { registerDestructor } from '@ember/destroyable';
import { guidFor } from '@ember/object/internals';
import { loadScript, loadStylesheet } from 'embercom/lib/dom-utils';
import { getTeammateAppUrls } from 'embercom/lib/react-islands';

import ENV from 'embercom/config/environment';

// The TypeScript global definitions in apps/embercom/types/embercom/index.d.ts are
// derived from this list (but this is only embercom's understanding of the world)
// For a render method to be accessible here it must also be implemented in:
// * apps/teammate-app/src/app/entry-island.tsx (Window['ReactTeammateApp']['experimental'])
export const MODIFIER_ACCESSIBLE_REACT_RENDER_METHODS = [
  'renderTutorialModalLauncher',
  'renderKnowledgeHubContentImportLogsSheet',
  'renderPlanComparisonDrawer',
  'renderAddWebsiteSheet',
  'renderAddNewExternalSourceSheet',
  'renderTestConfigPanel',
  'renderFinTasksEditorSheet',
  'renderAiProcedureModal',
  'renderUsageDrawer',
  'renderCardlessConversionModalLauncher',
  'renderDowngradeWarningModalLauncher',
  'renderTrialExtensionDialogLauncher',
  'renderHealthCheck',
  'renderTeammatePendingInvitesTable',
  'renderAdvancedSettingsWebsyncSheet',
  'renderReportingAgentSidebar',
  'renderAgentTypeSegmentControl',
  'renderEmailPreviewPanel',
  'renderFinPreviewPanel',
] as const;

export type ModifierAccessibleReactRenderMethod =
  (typeof MODIFIER_ACCESSIBLE_REACT_RENDER_METHODS)[number];
export type ModifierAccessibleReactComponentProps = Record<string, any>;
interface Args {
  Named: {
    renderMethod: ModifierAccessibleReactRenderMethod;
    componentProps: ModifierAccessibleReactComponentProps;
  };
}

interface RenderReactComponentSignature {
  Element: Element;
  Args: Args;
}

class RenderReactComponent extends Modifier<RenderReactComponentSignature> {
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;
  @service declare appService: $TSFixMe;
  @service declare session: Session;
  @service declare intl: IntlService;
  guid = guidFor(this);
  containerId = `react-component-container-${this.guid}`;
  hasShadowRoot = false;

  unmount?: Function;

  constructor(owner: any, args: any) {
    super(owner, args);

    registerDestructor(this, this.cleanup);
  }

  modify(element: Element, _positional: never, { renderMethod, componentProps }: Args['Named']) {
    this.renderReactComponent(element, renderMethod, componentProps);
  }

  cleanup = () => {
    this.unmount?.();
  };

  async renderReactComponent(
    element: Element,
    renderMethod: ModifierAccessibleReactRenderMethod,
    props: ModifierAccessibleReactComponentProps,
  ) {
    if (this.hasShadowRoot) {
      return;
    }

    if (ENV.environment === 'test') {
      return;
    }

    if (ENV.environment === 'development') {
      if (!this.reactTeammateAppIsland.loadedViteScripts) {
        await Promise.all([
          loadScript('http://embercom.test:4200/a/react-refresh.js'),
          loadScript('http://localhost:5173/r/@vite/client'),
        ]);

        this.reactTeammateAppIsland.loadedViteScripts = true;
      }
    }

    let { jsUrl, cssUrl } = getTeammateAppUrls();

    if (!this.reactTeammateAppIsland.loadedEntryPointScript) {
      await loadScript(jsUrl);

      this.reactTeammateAppIsland.loadedEntryPointScript = true;
    }

    if (!window.ReactTeammateApp) {
      throw new Error('ReactTeammateApp not found after loading script');
    }

    // Ensure bridge is set up for component islands (idempotent — guards with !this.reactTeammateApp)
    this.reactTeammateAppIsland.onAppReady();

    let reactComponentMountElement = document.createElement('div');
    reactComponentMountElement.id = this.containerId;
    reactComponentMountElement.style.position = 'relative';
    reactComponentMountElement.style.height = '100%';

    let shadowRoot = element.attachShadow({ mode: 'open' });
    this.hasShadowRoot = true;

    await loadStylesheet(cssUrl, shadowRoot);
    shadowRoot.appendChild(reactComponentMountElement);

    // Try to get appId from app service first, then session workspace as fallback
    let appId = this.appService.app?.id;
    if (!appId) {
      try {
        appId = this.session.workspace.id;
      } catch {
        // Workspace not loaded, appId will remain undefined
      }
    }

    let propsWithAppIdAndLocale = {
      ...props,
      appId,
      locale: this.intl.primaryLocale,
    };

    this.unmount = window.ReactTeammateApp.experimental[renderMethod](
      reactComponentMountElement,
      propsWithAppIdAndLocale,
    );
  }
}

export default RenderReactComponent;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'render-react-component': typeof RenderReactComponent;
  }
}
